import { useState, useEffect, useCallback } from 'react';
import { useSessionContext } from '@om/shared-auth';
import { celebrationService } from '@/lib/api/celebration.service';
import type { Celebration } from '@/types/celebration.types';

/**
 * Custom hook to manage active celebrations with automatic refresh
 */
export const useActiveCelebrations = () => {
  const sessionContext = useSessionContext();
  const doesSessionExist = !sessionContext.loading && sessionContext.doesSessionExist;
  const [activeCelebrations, setActiveCelebrations] = useState<Celebration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActiveCelebrations = useCallback(async () => {
    if (sessionContext.loading || !doesSessionExist) {
      setLoading(false);
      setError(null);
      setActiveCelebrations([]);
      return;
    }

    try {
      setError(null);
      setLoading(true);
      const celebrations = await celebrationService.getCelebrations();
      const safeCelebrations = Array.isArray(celebrations) ? celebrations : [];
      // Return celebrations (backend already excludes soft-deleted)
      const active = safeCelebrations.slice(0, 3);
      setActiveCelebrations(active);
    } catch (err) {
      console.error('Failed to fetch active celebrations:', err);
      setError('Failed to fetch celebrations');
      setActiveCelebrations([]);
    } finally {
      setLoading(false);
    }
  }, [doesSessionExist, sessionContext.loading]);

  useEffect(() => {
    if (sessionContext.loading || !doesSessionExist) {
      setLoading(false);
      setError(null);
      setActiveCelebrations([]);
      return;
    }

    void fetchActiveCelebrations();
  }, [doesSessionExist, fetchActiveCelebrations, sessionContext.loading]);

  const refresh = useCallback(() => {
    if (sessionContext.loading || !doesSessionExist) {
      setLoading(false);
      setError(null);
      setActiveCelebrations([]);
      return;
    }

    setLoading(true);
    void fetchActiveCelebrations();
  }, [doesSessionExist, fetchActiveCelebrations, sessionContext.loading]);

  return {
    activeCelebrations,
    loading,
    error,
    refresh,
    hasMaxLimit: activeCelebrations.length >= 3,
    canCreateNew: activeCelebrations.length < 3
  };
};
